import React, { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { Input } from 'components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { blogGenSearchPlayers } from '../../../services/api';

/** Popover + input combobox over /blogGen/players/search. `alternatives` are listed first. */
export default function PlayerSearch({ trigger, alternatives = [], initialQuery = '', onPick, team = null }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => {
      setLoading(true);
      blogGenSearchPlayers(q, team, 15)
        .then(setResults)
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, open, team]);

  const pick = (p) => {
    onPick(p);
    setOpen(false);
  };

  const Row = ({ p, hint }) => (
    <button
      type="button"
      className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-token-base hover:bg-muted"
      onClick={() => pick(p)}
    >
      <span>
        <span className="font-medium">{p.web_name}</span>{' '}
        <span className="text-muted-foreground">{p.first_name} {p.second_name}</span>
      </span>
      <span className="text-muted-foreground">
        {p.team_short} · {p.position} {hint}
      </span>
    </button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-[360px] p-2" align="start">
        <div className="relative mb-2">
          <Search size={14} className="absolute left-2 top-2.5 text-muted-foreground" />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search players…" className="h-8 pl-7" />
        </div>
        <div className="max-h-[260px] overflow-y-auto">
          {alternatives.length > 0 && !q && (
            <>
              <p className="px-2 py-1 text-[11px] uppercase text-muted-foreground">Suggestions</p>
              {alternatives.map((a) => (
                <Row key={`alt-${a.element}`} p={a} hint={a.score ? `· ${Math.round(a.score * 100)}%` : ''} />
              ))}
              <p className="px-2 py-1 text-[11px] uppercase text-muted-foreground">Search</p>
            </>
          )}
          {loading && <p className="px-2 py-1 text-token-base text-muted-foreground">Searching…</p>}
          {!loading && results.length === 0 && <p className="px-2 py-1 text-token-base text-muted-foreground">No players</p>}
          {results.map((p) => (
            <Row key={p.element} p={p} />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
